// Tenants Frontend App - Main entry point

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthProvider } from "@/hooks/useAuth";

// Lazy load page components for code splitting
const Login = lazy(() => import("@/pages/Login").then((m) => ({ default: m.Login })));
const TenantsList = lazy(() => import("@/pages/TenantsList").then((m) => ({ default: m.TenantsList })));
const TenantDetail = lazy(() => import("@/pages/TenantDetail").then((m) => ({ default: m.TenantDetail })));
const TenantCreate = lazy(() => import("@/pages/TenantCreate").then((m) => ({ default: m.TenantCreate })));
const Settings = lazy(() => import("@/pages/Settings").then((m) => ({ default: m.Settings })));

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Loading fallback component
function LoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/login" element={<Login />} />
                <Route
                  path="/tenants"
                  element={
                    <ProtectedRoute>
                      <TenantsList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tenants/new"
                  element={
                    <ProtectedRoute>
                      <TenantCreate />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tenants/:id"
                  element={
                    <ProtectedRoute>
                      <TenantDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route path="/" element={<Navigate to="/tenants" replace />} />
              </Route>
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
